import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios"
import { API_BASE_URL } from "@/config/api"
import { useAuthStore } from "@/stores/auth"
import type { APIResponse } from "@/types"

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  // 45s > los 30s del middleware (config.py middleware_timeout). Simetricos, el
  // front abortaba mientras el backend seguia vivo: el operador veia un error
  // sobre una venta que SI se aplico y reintentaba -> cobro doble.
  timeout: 45000,
})

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Singleton refresh lock to prevent concurrent refresh attempts
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: unknown) => void
  reject: (reason?: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<APIResponse<unknown>>) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

    // El logout se excluye del manejo de 401: si respondiera 401, la rama de
    // "sin refresh token" llamaría a logout() otra vez → nuevo POST → 401 →
    // bucle infinito de peticiones por cada cierre de sesión.
    const esLogout = (originalRequest?.url ?? "").includes("/auth/logout")

    // Handle 401 - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry && !esLogout) {
      if (isRefreshing) {
        // Another refresh is in flight — queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`
          }
          return api(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = useAuthStore.getState().refreshToken
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
            refresh_token: refreshToken,
          })

          if (response.data.success) {
            const { access_token, refresh_token } = response.data.tokens
            useAuthStore.getState().setTokens(access_token, refresh_token)
            processQueue(null, access_token)

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${access_token}`
            }
            return api(originalRequest)
          }
        } catch (refreshError) {
          processQueue(refreshError, null)
          useAuthStore.getState().logout()
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      } else {
        isRefreshing = false
        useAuthStore.getState().logout()
      }
    }

    return Promise.reject(error)
  }
)

// API helper functions
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.get<T>(url, config)
  return response.data
}

export async function post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.post<T>(url, data, config)
  return response.data
}

export async function put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.put<T>(url, data, config)
  return response.data
}

export async function patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.patch<T>(url, data, config)
  return response.data
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.delete<T>(url, config)
  return response.data
}

export async function uploadFile<T>(
  url: string,
  file: File,
  fieldName: string = "file",
  additionalData?: Record<string, string>
): Promise<T> {
  const formData = new FormData()
  formData.append(fieldName, file)
  
  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value)
    })
  }
  
  const response = await api.post<T>(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
  
  return response.data
}

export default api
